import {
  faAnglesRight,
  faChartScatter,
  faFileLines,
  faRotateLeft,
  faSparkles,
  faTimes,
} from "@fortawesome/pro-regular-svg-icons";
import { faRotateRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { HocuspocusProvider } from "@hocuspocus/provider";
import { useQuery } from "@tanstack/react-query";
import { useEditor } from "@tiptap/react";
import classNames from "classnames";
import ColorThief from "colorthief";
import parse, { domToReact } from "html-react-parser";
import { nanoid } from "nanoid";
import { forwardRef, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { ReactFlowProvider } from "reactflow";
import useLocalStorageState from "use-local-storage-state";
import * as Y from "yjs";

import { ListItemReadOnly, LLMSidebar } from "@/components";
import { loadExtensions } from "@/extensions";
import { useActiveEditor, useQueries, YdocProvider } from "@/hooks";
import { getPage } from "@/hooks/useQueries/pages";
import { findBlockById } from "@/utils/block-helpers";

import GraphReadOnly from "../Graph/GraphReadOnly";
import styles from "./Page.module.scss";

interface PageProps extends React.SelectHTMLAttributes<HTMLDivElement> {
  id: string | undefined;
  nodePageOpen?: boolean;
  isNodePage?: boolean;
}

export default forwardRef<HTMLDivElement, PageProps>(
  ({ id, className, nodePageOpen = false, isNodePage = false, ...props }, ref) => {
    let { projectPublicId } = useParams();

    const { setActiveEditor, id: activeId, setId } = useActiveEditor();

    const { currentProject } = useQueries();
    const [llmOpen, setLlmOpen] = useLocalStorageState<boolean>(
      `ldg-${currentProject?.slug}-public-llm-open${isNodePage ? `-${id}` : ""}`,
      { defaultValue: !isNodePage ? true : false }
    );
    const [graphOpen, setGraphOpen] = useLocalStorageState<boolean>(
      `ldg-${currentProject?.slug}-public-graph-open${isNodePage ? `-${id}` : ""}`,
      { defaultValue: !isNodePage ? true : false }
    );
    const [editorOpen, setEditorOpen] = useLocalStorageState<boolean>(
      `ldg-${currentProject?.slug}-public-editor-open${isNodePage ? `-${id}` : ""}`,
      { defaultValue: !isNodePage ? false : true }
    );

    const { data: page, isLoading } = useQuery({
      queryKey: ["pages", currentProject?.id, id],
      queryFn: ({ signal }) => getPage(signal, currentProject?.id, id),
      enabled: Boolean(currentProject?.id && id),
      retry: false,
    });

    useEffect(() => {
      const config = page?.meta?.publicConfig;
      if (config) {
        if ("showEditor" in config) setEditorOpen(config.showEditor);
        if ("showGraph" in config) setGraphOpen(config.showGraph);
        if ("showLLM" in config) setLlmOpen(config.showLLM);
      }
    }, [page]);

    const roomName = useMemo(() => {
      if (!currentProject?.slug || !id) return;
      return `lateral-ldg-${currentProject?.slug}-${id}`;
    }, [currentProject, id]);
    const ydoc = useMemo(() => {
      if (!roomName) return;
      return new Y.Doc({ guid: roomName });
    }, [roomName]);
    const provider = useMemo(() => {
      // console.log("provider, ", roomName);
      if (!roomName) return;
      return new HocuspocusProvider({
        url: import.meta.env.VITE_HOCUSPOCUS_URL,
        name: roomName,
        document: ydoc,
      });
    }, [roomName]);
    const extensions = loadExtensions(provider, ydoc, true);

    const [html, setHtml] = useState<React.ReactNode>();

    const editor = useEditor(
      {
        editable: false,
        extensions,
        editorProps: { pageId: id },
        onFocus: ({ editor }) => {
          setActiveEditor(editor);
          setId(id);
        },
        onCreate: ({ editor }) => {
          if (isLoading || !page) return;
          setActiveEditor(editor);
          setId(id);
        },
        onUpdate({ editor }) {
          const json = editor?.getJSON();
          // console.log("json", json);
          let index = 0;
          const options = {
            transform: (reactNode, domNode) => {
              if (domNode.name == "li") {
                // ...
                // console.log(reactNode, domNode);
                // console.log(domNode.attribs);
                const blockId = domNode.attribs.blockid;
                const node = findBlockById(json, blockId);
                index += 1;

                if (node?.content.length == 1 && !("content" in node?.content[0])) {
                  return;
                }

                return (
                  <ListItemReadOnly
                    key={`node-${index}`}
                    node={node}
                    pageId={id}
                    style={{ color: "hotpink" }}
                  >
                    {domToReact(domNode.children, options)}
                  </ListItemReadOnly>
                );
              } else {
                return reactNode;
              }
            },
          };

          setHtml(parse(editor.getHTML(), options));
        },
      },
      [id, isLoading]
    );

    if (!page || !editor || isLoading) {
      return (
        <div {...props} className={classNames(className, styles.border)}>
          <div className={styles.loader}>Loading...</div>
        </div>
      );
    }

    return (
      <div
        {...props}
        className={classNames(className)}
        style={
          nodePageOpen && isNodePage
            ? {
                width:
                  (editorOpen ? 1 : 0) + (llmOpen ? 1 : 0) + (graphOpen ? 1 : 0) > 1
                    ? "75%"
                    : "40%",
              }
            : {}
        }
      >
        <YdocProvider ydoc={ydoc}>
          <ReactFlowProvider>
            {isNodePage && (
              <div className={styles.close}>
                <Tooltip id="close" content="Close node page" className="tooltip" />
                <Link to={`/public/${projectPublicId}`} data-tooltip-id="close">
                  <FontAwesomeIcon icon={faAnglesRight} />
                </Link>
              </div>
            )}
            <div
              className={classNames(styles.paneToggles, {
                [styles.nodePageOpen]: nodePageOpen,
                [styles.isNodePage]: isNodePage,
              })}
              style={
                nodePageOpen && !isNodePage
                  ? {
                      // Hacky, yes but it works
                      left: (document?.getElementById("headerLeft")?.clientWidth ?? 0) + 32,
                    }
                  : {}
              }
            >
              <Tooltip id="editor" content="Toggle Editor" className="tooltip" />
              <button
                onClick={() => setEditorOpen(!editorOpen)}
                data-tooltip-id="editor"
                className={classNames({ [styles.active]: editorOpen })}
              >
                <FontAwesomeIcon icon={faFileLines} />
              </button>
              <div className={styles.divider}></div>
              <Tooltip id="graph" content="Toggle Graph" className="tooltip" />
              <button
                onClick={() => setGraphOpen(!graphOpen)}
                data-tooltip-id="graph"
                className={classNames({ [styles.active]: graphOpen })}
              >
                <FontAwesomeIcon icon={faChartScatter} />
              </button>
              <div className={styles.divider}></div>
              <Tooltip id="llm" content="Toggle LLM" className="tooltip" />
              <button
                onClick={() => setLlmOpen(!llmOpen)}
                data-tooltip-id="llm"
                className={classNames({ [styles.active]: llmOpen })}
              >
                <FontAwesomeIcon icon={faSparkles} />
              </button>
            </div>
            <div className={classNames(styles.columns)}>
              {editorOpen ? (
                <div
                  className={classNames(styles.wrapper, styles.editor)}
                  onClick={() => {
                    setActiveEditor(editor);
                    setId(id);
                  }}
                >
                  <div className={classNames(styles.scroll, styles.editorWrapper)}>
                    <div className={classNames("ProseMirror")}>{html}</div>
                  </div>
                </div>
              ) : (
                <></>
              )}

              {graphOpen && (
                <GraphReadOnly
                  pageId={id}
                  className={classNames(styles.wrapper)}
                  editor={editor}
                  ydoc={ydoc}
                  key={roomName}
                  active={id == activeId}
                />
              )}

              {llmOpen && (
                <LLMSidebar
                  className={classNames(styles.wrapper, styles.scroll)}
                  editor={editor}
                  page={page}
                />
              )}
            </div>
          </ReactFlowProvider>
        </YdocProvider>
      </div>
    );
  }
);
